'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProductCard from './ProductCard';
import Image from 'next/image';
import Link from 'next/link';
import { getName } from '@/libs/utils';

type Group = {
  _id: string;
  displayName: string;
  displayImage: string;
  lowestPrice: number;
};

type PaymentMethod = {
  _id: string;
  displayName: string;
  displayImage: string;
  enabled: boolean;
};

type FeedbackItem = {
  _id: string;
  positive: boolean;
  comment: string;
  createdAt: string;
};

export default function HeroSection({ initialProducts, recentRestocks, paymentMethods, feedbacks }: { initialProducts: Group[]; recentRestocks: Group[]; paymentMethods: PaymentMethod[]; feedbacks: FeedbackItem[] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const suggestions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return initialProducts
      .filter(g => (g.displayName ?? "").toLowerCase().includes(q))
      .slice(0, 5);
  }, [searchTerm, initialProducts]);

  const prepared = useMemo(
    () =>
      initialProducts.map(p => ({
        ...p,
        _lc: `${p.displayName ?? ""}`.toLowerCase(),
      })),
    [initialProducts]
  );

  function highlightMatch(text: string, query: string) {
    const q = query.trim();
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-transparent text-orange-300 underline">{text.slice(i, i + q.length)}</mark>
        {text.slice(i + q.length)}
      </>
    );
  }

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setOpen(false);
        }
      },
      {
        root: null,
        threshold: 1.0,
      }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <section className="pt-10 pb-5">
        <div className="container mx-auto flex flex-col items-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 px-5 text-center text-white">
            Welcome to <span className="font-extrabold bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent inline-block">{getName(false)}</span>
          </h1>
          <p className="mx-5 sm:text-2 sm:mx-5 text-lg max-w-xl text-center text-gray-500 mb-5">
            Shop our exclusive range of fashion-forward designs, made to align with your taste and highlight your individuality.
          </p>
          <div ref={wrapperRef} className="relative w-[90%] sm:w-full max-w-xl">
            <div className="flex items-center rounded-full border border-orange-600 px-4 py-2 shadow-xl">
              <svg className="mr-3 text-orange-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width={20} height={20} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>

              <input
                type="text"
                placeholder="Search"
                className="bg-transparent flex-1 outline-none text-white text-base"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setOpen(true); }}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 120)}
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls="suggestions"
              />
            </div>

            {open && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden rounded-2xl border border-orange-600/40 bg-slate-900/95 backdrop-blur shadow-2xl">
                <ul id="suggestions" role="listbox" className="divide-y divide-white/5">
                  {suggestions.slice(0, 3).map((g, idx) => (
                    <Link href={`/products/${g._id}`}>
                      <li
                        key={g._id}
                        role="option"
                        aria-selected={idx === active}
                        onMouseEnter={() => setActive(idx)}
                        className={`px-4 py-3 flex items-center gap-3 cursor-pointer ${idx === active ? "bg-orange-600/20" : "hover:bg-orange-600/10"
                          }`}
                      >
                        {g.displayImage && (
                          <Image src={g.displayImage} width={420} height={320} alt="" className="h-10 w-20 rounded-md" priority />
                        )}
                        <div className="min-w-0">
                          <div className="truncate text-white">{highlightMatch(g.displayName, searchTerm)}</div>
                          {typeof g.lowestPrice === "number" && g.lowestPrice > 0 && (
                            <div className="text-xs text-white/70">From ${g.lowestPrice.toFixed(2)}</div>
                          )}
                        </div>
                      </li>
                    </Link>
                  ))}
                </ul>
              </div>
            )}

          </div>
        </div>
      </section>


      <section className={`container mx-auto max-w-[1280px] px-5 py-10 ${open ? "sm:py-30" : "sm:py-12"} 2xl:px-0`}>
        <div className="lg:px-16 mb-10">
          <div className="my-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
                Best <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent">Sellers</span>
              </h2>
              <p className="text-gray-400">Check out our latest restocked products</p>
            </div>
            <Link href="/products">
              <button className="relative px-6 py-2 text-white font-semibold rounded-full backdrop-blur-lg bg-white/10 border border-white/20 shadow-lg overflow-hidden transition-all duration-500 hover:scale-105 hover:bg-white/20">
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-orange/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 blur-xl"></span>
                <span className="absolute -inset-[2px] rounded-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 opacity-30 blur-lg"></span>
                <span className="relative z-10 cursor-pointer flex items-center gap-2 text-white">
                  View All
                  <svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="h-auto w-[5px] sm:w-[6px] text-white">
                    <path d="M0.695312 8.75L4.44531 5L0.695312 1.25" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" className="stroke-primary"></path>
                  </svg>
                </span>
              </button>
            </Link>
          </div>
          <div className="flex justify-center gap-[20px] flex-wrap">
            {prepared.slice(0, 4).map((group) => (
              <ProductCard
                key={group._id.toString()}
                categoryId={group._id.toString()}
                name={group.displayName}
                price={group.lowestPrice}
                imageUrl={group.displayImage}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-[1280px] px-5 py-10 2xl:px-0">
        <div className="lg:px-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
                Recent <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent">Restocks</span>
              </h2>
              <p className="text-gray-400">Check out our latest restocked products</p>
            </div>
            <Link href="/products">
              <button className="relative px-6 py-2 text-white font-semibold rounded-full backdrop-blur-lg bg-white/10 border border-white/20 shadow-lg overflow-hidden transition-all duration-500 hover:scale-105 hover:bg-white/20">
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-orange/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 blur-xl"></span>
                <span className="absolute -inset-[2px] rounded-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 opacity-30 blur-lg"></span>
                <span className="relative z-10 cursor-pointer flex items-center gap-2 text-white">
                  View All
                  <svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="h-auto w-[5px] sm:w-[6px] text-white">
                    <path d="M0.695312 8.75L4.44531 5L0.695312 1.25" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" className="stroke-primary"></path>
                  </svg>
                </span>
              </button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {recentRestocks.slice(0, 4).map((group) => (
              <ProductCard
                key={group._id.toString()}
                categoryId={group._id.toString()}
                name={group.displayName}
                price={group.lowestPrice}
                imageUrl={group.displayImage}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-[1280px] px-5 py-10 2xl:px-0">
        <div className="lg:px-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
              Premium Quality offered at {getName(false)}
            </h2>
            <p className="text-gray-400">Forged from the ground up with performance, reliability, and elegance as our foundation.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[rgba(249,115,22,0.1)] border border-orange-600/25 rounded-2xl p-6 text-center hover:border-orange-600/50 transition-all">
              <div className="w-18 h-16 mx-auto mb-4 rounded-full bg-orange-600/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">24/7 Support</h3>
              <p className="text-gray-400">Round-the-clock customer assistance whenever you need it</p>
            </div>
            <div className="bg-[rgba(249,115,22,0.1)] border border-orange-600/25 rounded-2xl p-6 text-center hover:border-orange-600/50 transition-all">
              <div className="w-18 h-16 mx-auto mb-4 rounded-full bg-orange-600/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Secure Payments</h3>
              <p className="text-gray-400">Your transactions are protected with industry-standard encryption</p>
            </div>
            <div className="bg-[rgba(249,115,22,0.1)] border border-orange-600/25 rounded-2xl p-6 text-center hover:border-orange-600/50 transition-all">
              <div className="w-18 h-16 mx-auto mb-4 rounded-full bg-orange-600/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Instant Delivery</h3>
              <p className="text-gray-400">Get your digital products delivered immediately after purchase</p>
            </div>
          </div>
        </div>
      </section>

      <ReviewsMarquee feedbacks={feedbacks} />

      <section className="container mx-auto max-w-[1280px] px-5 py-10 2xl:px-0">
        <div className="lg:px-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
              Accepted <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent">Payment Methods</span>
            </h2>
            <p className="text-gray-400">We accept variaty of payment methods.</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {paymentMethods.map((payment) => (
              <>
                <PaymentMethodItem
                  name={payment.displayName}
                  image={payment.displayImage}
                />
              </>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-[1280px] px-5 py-10 2xl:px-0">
        <div className="lg:px-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
              Frequently Asked <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent">Questions</span>
            </h2>
            <p className="text-gray-400">Find answers to common questions about our platform</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            <FAQItem 
              question="What payment methods do you accept?"
              answer="We accept all major cryptocurrencies including Bitcoin, Ethereum, Litecoin, and stablecoins (USDT, USDC). Payments are processed instantly and securely through encrypted channels."
            />
            <FAQItem 
              question="How quickly will I receive my order?"
              answer="Digital products are delivered instantly to your account after payment confirmation. You can access your purchases immediately from your order history, available 24/7."
            />
            <FAQItem 
              question="Is my purchase secure and private?"
              answer="Absolutely. All transactions are encrypted and processed through industry-leading secure payment gateways. We prioritize your privacy and never share your information with third parties."
            />
            <FAQItem 
              question="Do you offer customer support?"
              answer="Yes! Our support team is available 24/7 to assist you. Reach us through live chat, contact form, or your account dashboard for fast and reliable help."
            />
          </div>
        </div>
      </section>
    </>
  );
}

function PaymentMethodItem({name, image}: {name:string; image: string}) {
  return (
    <div className="group relative">
      <div className="bg-[rgba(249,115,22,0.1)] border-2 border-gray-800 rounded-xl p-4 transition-all duration-300 hover:border-orange-600/50 hover:bg-[rgba(249,115,22,0.1)] hover:scale-105 cursor-pointer">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-600/20 to-purple-600/20 flex items-center justify-center overflow-hidden flex-shrink-0">
            <Image
              src={"/assets/" + image + ".png"}
              alt={name}
              width={32}
              height={32}
              className="object-contain"
            />
          </div>
          <span className="text-sm font-semibold text-white truncate">
            {name}
          </span>
        </div>
      </div>
    </div>
  );
}

function ReviewsMarquee({ feedbacks }: { feedbacks: FeedbackItem[] }) {
  const selected = useMemo(() => {
    if (feedbacks.length === 0) return [];

    // Separate positive and negative
    const positive = feedbacks.filter(f => f.positive);
    const negative = feedbacks.filter(f => !f.positive);

    const shuffled: FeedbackItem[] = [];

    // Pick mostly positive: up to 85% positive, rest negative
    const total = Math.min(Math.max(5, feedbacks.length), 20);
    const positiveCount = Math.min(positive.length, Math.ceil(total * 0.85));
    const negativeCount = Math.min(negative.length, total - positiveCount);

    // Shuffle helper
    const shuffle = <T,>(arr: T[]): T[] => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    shuffled.push(...shuffle(positive).slice(0, positiveCount));
    shuffled.push(...shuffle(negative).slice(0, negativeCount));

    return shuffle(shuffled);
  }, [feedbacks]);

  // Overall rating: 5 = all positive, 0 = all negative
  const overallRating = useMemo(() => {
    if (feedbacks.length === 0) return 0;
    const positiveRatio = feedbacks.filter(f => f.positive).length / feedbacks.length;
    return Math.round(positiveRatio * 50) / 10; // 0.0 - 5.0
  }, [feedbacks]);

  if (selected.length === 0) return null;

  // Duplicate items for seamless infinite scroll
  const items = [...selected, ...selected];

  return (
    <section className="container mx-auto max-w-[1280px] px-5 py-10 2xl:px-0">
      <div className="lg:px-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">
            What Our <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent">Customers</span> Say
          </h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(star => {
                const filled = overallRating >= star;
                const half = !filled && overallRating >= star - 0.5;
                return (
                  <svg key={star} className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                    {filled ? (
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#f97316" />
                    ) : half ? (
                      <>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#374151" />
                        <path d="M12 2v15.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#f97316" />
                      </>
                    ) : (
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#374151" />
                    )}
                  </svg>
                );
              })}
            </div>
            <span className="text-xl font-bold text-white">{overallRating.toFixed(1)}</span>
            <span className="text-gray-400 text-sm">/ 5.0</span>
            <span className="text-gray-500 text-sm ml-1">({feedbacks.length} reviews)</span>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#202020] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#202020] to-transparent z-10 pointer-events-none" />

          <div
            className="flex gap-4 w-max"
            style={{
              animation: `marqueeScroll ${selected.length * 4}s linear infinite`,
            }}
          >
            {items.map((f, i) => (
              <div
                key={`${f._id}-${i}`}
                className={`flex-shrink-0 w-[280px] sm:w-[320px] bg-[rgba(249,115,22,0.1)] border rounded-2xl p-5 ${
                  f.positive ? "border-green-500/25" : "border-red-500/25"
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    f.positive ? "bg-green-500/20" : "bg-red-500/20"
                  }`}>
                    {f.positive ? (
                      <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-white">Anonymous Customer</span>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed line-clamp-3">{f.comment}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link
            href="/reviews"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm font-semibold transition-colors"
          >
            View all reviews
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="bg-[rgba(249,115,22,0.1)] border border-orange-600/25 rounded-xl overflow-hidden hover:border-orange-600/50 transition-all">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-5 text-left focus:outline-none"
      >
        <span className="text-lg font-semibold text-white pr-4">{question}</span>
        <svg
          className={`w-6 h-6 text-orange-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-gray-400 border-t border-gray-800 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}
